import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../utils/api';
import { logWarn } from '../utils/log';

const TeamContext = createContext(null);

export function TeamProvider({ getToken, userRole, children }) {
  const [teams, setTeams] = useState([]);
  const [activeTeamId, setActiveTeamId] = useState(() => {
    try { return sessionStorage.getItem('flowcore_active_team') || ''; } catch { return ''; }
  });
  const [loading, setLoading] = useState(true);
  const activeTeamIdRef = useRef(activeTeamId);
  activeTeamIdRef.current = activeTeamId;

  const fetchTeams = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      if (!token) return;
      const res = await fetch(`${API_BASE_URL}/api/teams/my-teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      const list = data.teams || [];
      setTeams(list);
      // Auto-select first team if none active or active not in list
      if (list.length > 0) {
        const currentId = activeTeamIdRef.current;
        const found = list.find((t) => t.id === currentId);
        if (!found) {
          // Admins default to Lab (isDefault), members to their first team
          const def = userRole === 'admin'
            ? (list.find((t) => t.isDefault) || list[0])
            : (list.find((t) => !t.isDefault) || list[0]);
          setActiveTeamId(def.id);
          try {
            sessionStorage.setItem('flowcore_active_team', def.id);
            sessionStorage.setItem('flowcore_active_team_default', def.isDefault ? '1' : '');
          } catch {}
        }
      } else {
        // No teams available — clear stale selection
        setActiveTeamId('');
        try { sessionStorage.removeItem('flowcore_active_team'); } catch {}
      }
    } catch (e) {
      logWarn('teams', 'fetchTeams:', e?.message || e);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchTeams(); }, [fetchTeams]);

  const switchTeam = useCallback((teamId) => {
    setActiveTeamId(teamId);
    try {
      sessionStorage.setItem('flowcore_active_team', teamId);
      const t = teams.find((tm) => tm.id === teamId);
      sessionStorage.setItem('flowcore_active_team_default', t?.isDefault ? '1' : '');
    } catch {}
  }, [teams]);

  const activeTeam = teams.find((t) => t.id === activeTeamId) || teams[0] || null;

  return (
    <TeamContext.Provider value={{
      teams,
      activeTeam,
      activeTeamId: activeTeam?.id || '',
      switchTeam,
      refreshTeams: fetchTeams,
      loading,
      userRole
    }}>
      {children}
    </TeamContext.Provider>
  );
}

export function useTeam() {
  const ctx = useContext(TeamContext);
  if (!ctx) throw new Error('useTeam must be used within TeamProvider');
  return ctx;
}
